import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import dayjs from 'dayjs'; 
import {
  Box, Typography, Button, Grid, Card, CardContent, CardActions,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, 
  InputAdornment, MenuItem, Alert, Chip, Avatar, Divider, Paper,
  Tooltip, CircularProgress, List, ListItem, ListItemText,
  FormControl, InputLabel, Select 
} from '@mui/material';

// ICONOS
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderSharedIcon from '@mui/icons-material/FolderShared'; 
import PersonIcon from '@mui/icons-material/Person';
import WarningIcon from '@mui/icons-material/Warning';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// SERVICIOS
import { reorderQueue } from '../services/reportService';

const API_URL = process.env.REACT_APP_API_URL || 'https://luiatro-health-cloud-production.up.railway.app';

interface Patient {
  id: string;
  name: string;
  lastName: string;
  cedula?: string;
  idNumber?: string;
  age?: string;
  birthDate?: string;
  gender?: string;
  phone?: string;
  email?: string;
  address?: string;
  createdBy?: string;
  pendingStudies?: any[];
  stateId?: number | string | null;
  municipalityId?: number | string | null;
  isWaiting?: boolean;
  waitingSince?: string | Date;
  queuePosition?: number;
}

const PatientsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const userRole = (user as any)?.role;
  const isAdmin = userRole === 'administrador';
  
  const [patients, setPatients] = useState<Patient[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditingMode, setIsEditingMode] = useState(false);
  const [currentPatientId, setCurrentPatientId] = useState<string | null>(null);
  const [error, setError] = useState('');
  
  const [luiatroDialog, setLuiatroDialog] = useState<{ open: boolean; content: string; onConfirm: any }>({ 
      open: false, content: '', onConfirm: null 
  });


// --- NUEVOS ESTADOS PARA EL MODAL DE VISITAS ---
  const [visitsOpen, setVisitsOpen] = useState(false);
  const [selectedPatientVisits, setSelectedPatientVisits] = useState<any[]>([]);
  const [loadingVisits, setLoadingVisits] = useState(false);

  // Función para cargar el historial rápido de visitas
  const handleOpenVisits = async (patient: Patient) => {
    setLoadingVisits(true);
    setVisitsOpen(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/evolutions/${patient.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setSelectedPatientVisits(await res.json());
      }
    } catch (e) { console.error(e); }
    setLoadingVisits(false);
  };

  // ✅ ESTADO DEL FORMULARIO RESTAURADO (10 CAMPOS)
  const [patientForm, setPatientForm] = useState({
    name: '',
    lastName: '',
    cedula: '',
    birthDate: '',
    gender: '',
    phone: '',
    email: '',
    address: '',
    stateId: '' as string | number,
    municipalityId: '' as string | number
  });

  const [states, setStates] = useState<any[]>([]);
  const [municipalities, setMunicipalities] = useState<any[]>([]);

  // --- CARGA DE DATOS ---
  useEffect(() => { 
    fetchPatients(); 
    fetchStates(); 
  }, []);

  const fetchPatients = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/patients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setPatients(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchStates = async () => {
    try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/locations/states`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) setStates(await res.json());
    } catch (e) { console.error(e); }
  };

  // Cargar municipios cuando cambie el estado seleccionado
  useEffect(() => {
    const fetchMunicipalities = async () => {
        if (!patientForm.stateId) {
            setMunicipalities([]);
            return;
        }
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/locations/municipalities/${patientForm.stateId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) setMunicipalities(await res.json());
    };
    fetchMunicipalities();
  }, [patientForm.stateId]);

  // --- MANEJADORES ---
  const handleOpenHistory = (id: string) => {
    const path = userRole === 'asistente' ? `/triage/${id}` : `/medical-history/${id}`;
    navigate(path);
  };

  const handleOpenCreate = () => {
      setPatientForm({ name: '', lastName: '', cedula: '', birthDate: '', gender: '', phone: '', email: '', address: '', stateId: '', municipalityId: ''});
      setIsEditingMode(false);
      setCurrentPatientId(null);
      setError('');
      setOpenDialog(true);
  };

  const handleOpenEdit = (patient: Patient) => {
      setPatientForm({
          name: patient.name || '',
          lastName: patient.lastName || '',
          cedula: patient.cedula || patient.idNumber || '',
          birthDate: patient.birthDate ? dayjs(patient.birthDate).format('YYYY-MM-DD') : '',
          gender: patient.gender || '',
          phone: patient.phone || '',
          email: patient.email || '',
          address: patient.address || '',
          stateId: patient.stateId || '',
          municipalityId: patient.municipalityId || ''
      });
      setIsEditingMode(true);
      setCurrentPatientId(patient.id);
      setError('');
      setOpenDialog(true);
  };

  const handleSave = async () => {
    if (!patientForm.name || !patientForm.lastName || !patientForm.cedula) {
      setError("Nombre, Apellido y Cédula son obligatorios.");
      return;
    }

    const bodyData: any = { 
        ...patientForm,
        stateId: patientForm.stateId ? parseInt(patientForm.stateId.toString()) : null,
        municipalityId: patientForm.municipalityId ? parseInt(patientForm.municipalityId.toString()) : null
    };

    if (!isEditingMode) {
        bodyData.createdBy = (user as any)?.id;
    }

    const method = isEditingMode ? 'PUT' : 'POST';
    const url = isEditingMode ? `${API_URL}/api/patients/${currentPatientId}` : `${API_URL}/api/patients`;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(url, {
        method: method,
        headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(bodyData)
      });

      if (res.ok) {
        const savedPatient = await res.json(); 
        setOpenDialog(false);
        if (!isEditingMode) {
            handleAttendToday(savedPatient.id, true);
        } else {
            fetchPatients();
        }
      } else {
        const errData = await res.json();
        setError(errData.error || "Error al guardar.");
      }
    } catch (e) { setError("Error de conexión."); }
  };

  const handleDelete = (id: string) => {
    setLuiatroDialog({
        open: true,
        content: "¿Está seguro de ELIMINAR este paciente? Solo podrá hacerlo si no tiene registros históricos vinculados.",
        onConfirm: async () => {
            try {
                const token = localStorage.getItem('token');
                const res = await fetch(`${API_URL}/api/patients/${id}`, { 
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    fetchPatients();
                    setLuiatroDialog(prev => ({ ...prev, open: false }));
                } else {
                    alert("No se puede eliminar: El paciente tiene registros vinculados.");
                    setLuiatroDialog(prev => ({ ...prev, open: false }));
                }
            } catch (e) { alert("Error al eliminar."); }
        }
    });
  };

  const handleAttendToday = async (id: string, status: boolean) => {
    try {
        const token = localStorage.getItem('token');
        await fetch(`${API_URL}/api/patients/${id}/waiting`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ isWaiting: status })
        });
        fetchPatients();
    } catch (e) { console.error(e); }
  };

  const handleMoveQueue = async (index: number, direction: 'up' | 'down') => {
    const newQueue = [...waitingPatients];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newQueue.length) return;

    [newQueue[index], newQueue[targetIndex]] = [newQueue[targetIndex], newQueue[index]];
    const orderedIds = newQueue.map(p => p.id);

    try {
        await reorderQueue(orderedIds);
        fetchPatients();
    } catch (e) { console.error(e); }
  };

  // --- FILTROS Y ORDEN ---
  const waitingPatients = useMemo(() => {
    return patients
      .filter(p => p.isWaiting)
      .sort((a, b) => (a.queuePosition || 0) - (b.queuePosition || 0));
  }, [patients]);

  const directoryResults = useMemo(() => {
    if (searchTerm.trim() === '') return [];
    const term = searchTerm.toLowerCase();
    return patients.filter(p => 
      `${p.name} ${p.lastName}`.toLowerCase().includes(term) || 
      (p.cedula || '').toLowerCase().includes(term)
    );
  }, [searchTerm, patients]);

// --- RENDER TARJETA ---
  const renderPatientCard = (patient: Patient, isInWaitingRoom: boolean) => {
    const isCreator = (user as any)?.id === patient.createdBy;
    const canEditBasicData = isAdmin || isCreator;
    const canDelete = isAdmin;
    const pendingCount = patient.pendingStudies?.length || 0;
    const currentIndex = waitingPatients.findIndex(p => p.id === patient.id);

    return (
        <Card elevation={isInWaitingRoom ? 6 : 2} sx={{ 
            height: '100%', display: 'flex', flexDirection: 'column', 
            borderTop: isInWaitingRoom ? '5px solid #2e7d32' : '4px solid #1976d2',
            position: 'relative'
        }}>
            <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
					<Box sx={{ display: 'flex', alignItems: 'center' }}>
						<Avatar sx={{ bgcolor: isInWaitingRoom ? '#2e7d32' : 'primary.main', width: 40, height: 40 }}>
							{patient.name.charAt(0)}
						</Avatar>
                        {isInWaitingRoom && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', ml: 1, borderLeft: '1px solid #ddd', pl: 0.5 }}>
                                <IconButton size="small" onClick={() => handleMoveQueue(currentIndex, 'up')} disabled={currentIndex === 0} sx={{ p: 0 }}>
                                    <ExpandLessIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={() => handleMoveQueue(currentIndex, 'down')} disabled={currentIndex === waitingPatients.length - 1} sx={{ p: 0 }}>
                                    <ExpandMoreIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        )}
                    </Box>

                    {/* ✅ SECCIÓN DE INDICADORES Y BOTÓN DE VISITAS */}
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        
                        {/* BOTÓN PARA VER ÚLTIMAS CONSULTAS Y VIGENCIA */}
                        <Tooltip title="Ver vigencia de consulta (Historial de visitas)">
                            <IconButton 
                                size="small" 
                                onClick={() => handleOpenVisits(patient)} 
                                sx={{ 
                                    bgcolor: 'rgba(0,0,0,0.05)', 
                                    color: isInWaitingRoom ? '#2e7d32' : '#1976d2',
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.1)' }
                                }}
                            >
                                <HistoryIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        {isInWaitingRoom && (
                            <Chip label={dayjs(patient.waitingSince).format('hh:mm A')} size="small" color="success" variant="outlined" />
                        )}
                        {pendingCount > 0 && (
                            <Chip icon={<WarningIcon style={{color: 'white'}} />} label={`${pendingCount} Pnd`} color="warning" size="small" />
                        )}
                    </Box>
                </Box>
                
                <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.2, mb: 0.5 }}>
                    {patient.name} {patient.lastName}
                </Typography>
                <Typography color="text.secondary" variant="body2">C.I.: {patient.cedula || 'S/C'}</Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2"><strong>Edad:</strong> {patient.birthDate ? dayjs().diff(dayjs(patient.birthDate), 'year') + ' años' : 'N/A'}</Typography>
                <Typography variant="body2" noWrap><strong>Telf:</strong> {patient.phone || 'N/A'}</Typography>
            </CardContent>
            
            <CardActions sx={{ bgcolor: isInWaitingRoom ? '#f1f8e9' : '#f5f5f5', p: 1.5 }}>
                <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                    <Button size="small" variant="contained" color={isInWaitingRoom ? "success" : "primary"} startIcon={<FolderSharedIcon />} onClick={() => handleOpenHistory(patient.id)} fullWidth>
                        {isInWaitingRoom ? "ATENDER" : "HISTORIA"}
                    </Button>
                    {!isInWaitingRoom ? (
                        <Button size="small" variant="outlined" color="success" onClick={() => handleAttendToday(patient.id, true)} disabled={patient.isWaiting}>
                            {patient.isWaiting ? "En espera" : "Atender Hoy"}
                        </Button>
                    ) : (
                        <IconButton size="small" color="error" onClick={() => handleAttendToday(patient.id, false)} title="Quitar">
                            <DeleteIcon />
                        </IconButton>
                    )}
                </Box>
            </CardActions>
            
            {!isInWaitingRoom && (
                <Box sx={{ position: 'absolute', bottom: 65, right: 8 }}>
                    {canEditBasicData && (
                        <IconButton size="small" color="info" onClick={() => handleOpenEdit(patient)} sx={{ bgcolor: 'white', mb: 0.5, boxShadow: 1 }}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    )}
                    {canDelete && (
                        <IconButton size="small" color="error" onClick={() => handleDelete(patient.id)} sx={{ bgcolor: 'white', boxShadow: 1 }}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    )}
                </Box>
            )}
        </Card>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Typography variant="h4" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
            <PersonIcon fontSize="large" /> Sala de Espera y Directorio
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>Nuevo Paciente</Button>
      </Box>

      <TextField 
        fullWidth variant="outlined" placeholder="Busque un paciente para anotar hoy..."
        value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 4, bgcolor: 'white', borderRadius: 1, boxShadow: 1 }}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon color="primary" /></InputAdornment>,
          endAdornment: searchTerm && <IconButton onClick={() => setSearchTerm('')}><CloseIcon /></IconButton>
        }}
      />

      {/* SALA DE ESPERA */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#2e7d32', fontWeight: 'bold' }}>
            <Chip label={waitingPatients.length} color="success" size="small" /> Pacientes en Espera para Hoy
        </Typography>
        <Divider sx={{ mb: 3, borderBottomWidth: 3, borderColor: '#2e7d32' }} />
        <Grid container spacing={3}>
            {waitingPatients.length === 0 ? (
                <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', bgcolor: '#fafafa', borderStyle: 'dashed' }}>
                        <Typography color="text.secondary">No hay pacientes en espera. Use el buscador para añadir uno.</Typography>
                    </Paper>
                </Grid>
            ) : (
                waitingPatients.map((p) => <Grid item xs={12} sm={6} md={4} lg={3} key={p.id}>{renderPatientCard(p, true)}</Grid>)
            )}
        </Grid>
      </Box>

      {/* RESULTADOS DIRECTORIO */}
      {searchTerm.trim() !== '' && (
        <Box>
            <Typography variant="h6" gutterBottom color="text.secondary" sx={{ fontWeight: 'bold' }}>🔍 Resultados del Directorio General</Typography>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
                {directoryResults.length === 0 ? (
                    <Grid item xs={12}><Alert severity="info">No se encontraron pacientes.</Alert></Grid>
                ) : (
                    directoryResults.map((p) => <Grid item xs={12} sm={6} md={4} lg={3} key={p.id}>{renderPatientCard(p, false)}</Grid>)
                )}
            </Grid>
        </Box>
      )}

      {/* ✅ DIÁLOGO DE REGISTRO COMPLETO RESTAURADO (10 CAMPOS) */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: isEditingMode ? 'info.main' : 'primary.main', color: 'white' }}>
            {isEditingMode ? 'Editar Datos del Paciente' : 'Registrar Nuevo Paciente'}
        </DialogTitle>
        <DialogContent dividers>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} md={6}>
                  <TextField autoFocus label="Nombres" fullWidth required value={patientForm.name} onChange={(e) => setPatientForm({...patientForm, name: e.target.value})} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField label="Apellidos" fullWidth required value={patientForm.lastName} onChange={(e) => setPatientForm({...patientForm, lastName: e.target.value})} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField label="Cédula (ID)" fullWidth required value={patientForm.cedula} onChange={(e) => setPatientForm({...patientForm, cedula: e.target.value})} />
                </Grid>
                <Grid item xs={6} md={3}>
                    <TextField label="Fecha de Nacimiento" fullWidth type="date" InputLabelProps={{ shrink: true }} value={patientForm.birthDate} onChange={(e) => setPatientForm({...patientForm, birthDate: e.target.value})} />
                </Grid>
                <Grid item xs={6} md={3}>
                    <FormControl fullWidth>
                        <InputLabel>Sexo</InputLabel>
                        <Select label="Sexo" value={patientForm.gender} onChange={(e) => setPatientForm({...patientForm, gender: e.target.value})}>
                            <MenuItem value="Masculino">Masculino</MenuItem>
                            <MenuItem value="Femenino">Femenino</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField label="Teléfono" fullWidth value={patientForm.phone} onChange={(e) => setPatientForm({...patientForm, phone: e.target.value})} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField label="Correo" fullWidth type="email" value={patientForm.email} onChange={(e) => setPatientForm({...patientForm, email: e.target.value})} />
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                      <InputLabel>Estado</InputLabel>
                      <Select label="Estado" value={patientForm.stateId} onChange={(e) => setPatientForm({ ...patientForm, stateId: e.target.value, municipalityId: '' })}>
                        {states.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                      </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth disabled={!patientForm.stateId}>
                      <InputLabel>Municipio</InputLabel>
                      <Select label="Municipio" value={patientForm.municipalityId} onChange={(e) => setPatientForm({ ...patientForm, municipalityId: e.target.value })}>
                        {municipalities.map((m) => <MenuItem key={m.id} value={m.id}>{m.name} {m.capital ? `(${m.capital})` : ''}</MenuItem>)}
                      </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField label="Dirección" fullWidth value={patientForm.address} onChange={(e) => setPatientForm({ ...patientForm, address: e.target.value })} />
                </Grid>
            </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setOpenDialog(false)} color="secondary">Cancelar</Button>
            <Button onClick={handleSave} variant="contained" color={isEditingMode ? 'info' : 'primary'}>
                {isEditingMode ? 'Actualizar' : 'Guardar'}
            </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={luiatroDialog.open} onClose={() => setLuiatroDialog(prev => ({...prev, open: false}))}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#d32f2f', fontWeight: 'bold' }}>
            <WarningIcon /> Luiatro Health dice:
        </DialogTitle>
        <DialogContent dividers><Typography>{luiatroDialog.content}</Typography></DialogContent>
        <DialogActions>
            <Button onClick={() => setLuiatroDialog(prev => ({...prev, open: false}))}>Cancelar</Button>
            <Button variant="contained" color="error" onClick={luiatroDialog.onConfirm}>Confirmar</Button>
        </DialogActions>
      </Dialog>
{/* ✅ MODAL DE VIGENCIA DE CONSULTA (15 DÍAS) */}
      <Dialog open={visitsOpen} onClose={() => setVisitsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon color="primary" /> Historial Reciente de Visitas
        </DialogTitle>
        <DialogContent dividers>
          {loadingVisits ? (
            <Box sx={{ textAlign: 'center', p: 3 }}><CircularProgress /></Box>
          ) : selectedPatientVisits.length === 0 ? (
            <Typography align="center" sx={{ py: 3 }} color="text.secondary">No se registran visitas anteriores.</Typography>
          ) : (
            <List>
              {selectedPatientVisits.map((visit, idx) => {
                const diffDays = dayjs().diff(dayjs(visit.date), 'day');
                const isVigente = diffDays <= 15;

                return (
                  <ListItem key={idx} divider sx={{ bgcolor: isVigente ? '#f1f8e9' : 'transparent' }}>
                    <ListItemText 
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography fontWeight="bold">{dayjs(visit.date).format('DD/MM/YYYY')}</Typography>
                          <Chip 
                            label={isVigente ? `VIGENTE (${diffDays} días)` : `Hace ${diffDays} días`} 
                            size="small" 
                            color={isVigente ? "success" : "default"}
                          />
                        </Box>
                      }
                      secondary={
  <Typography variant="body2" color="text.primary" sx={{ mt: 0.5 }}>
    {/* Probamos todas las variaciones posibles de nombre para que no falle */}
    <strong>Motivo:</strong> {visit.consultationReason || visit.consultationreason || 'No especificado'}
  </Typography>
}
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVisitsOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PatientsPage;